"use client"

import type { Product } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import Link from "next/link"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import { useState, useEffect } from "react"
import { useCart } from "@/lib/cart-context"
import { useLanguage } from "@/lib/language-context"
import { Price } from "@/components/price"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2, ArrowLeft, ShoppingCart, Plus, Minus, Heart, Check, Mail, Phone, User, Building } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"


interface ProductDetailProps {
  product: Product
  images: any[]
}

export function ProductDetail({ product, images }: ProductDetailProps) {
  // Parse images from comma-separated string in image_url or use fallback
  const rawImages = product.image_url ? product.image_url.split(',').map(u => u.trim()).filter(Boolean) : []

  // Also consider images from the separate table if they exist
  const extraImages = images?.map((img: any) => img.image_url || img.url).filter(Boolean) || []

  const allRawImages = [...rawImages, ...extraImages]

  // Deduplicate by base URL (ignoring query params like width) to fix scraper inefficiency
  const uniqueImagesMap = new Map()

  allRawImages.forEach(url => {
    try {
      const baseUrl = url.split('?')[0]
      // If we haven't seen this base URL, add it.
      // Optional: We could try to parse 'width' param and keep the largest, 
      // but for now, first-come (or last-come) is better than duplicates.
      // The provided list seems to have 300, 165, 360, 450. 
      // We probably want the largest or the default.
      // Let's just keep the first occurrence for simplicity, or overwrite if we want to be fancy.
      if (!uniqueImagesMap.has(baseUrl)) {
        uniqueImagesMap.set(baseUrl, url)
      }
    } catch (e) {
      // Fallback for invalid URLs
      if (!uniqueImagesMap.has(url)) uniqueImagesMap.set(url, url)
    }
  })

  const distinctImages = Array.from(uniqueImagesMap.values())

  const mainImage = distinctImages[0] || "/placeholder.svg?height=500&width=500&query=industrial part"
  const allImages = distinctImages.length > 0 ? distinctImages : [mainImage]

  const [selectedImage, setSelectedImage] = useState(mainImage)

  const [quantity, setQuantity] = useState(1)
  const [addedToCart, setAddedToCart] = useState(false)
  const { addToCart, addToWishlist, wishlist } = useCart()
  const { t } = useLanguage()
  const isInWishlist = wishlist.some((item) => item.id === product.id)
  const supabase = createClient()

  const [bulkFormData, setBulkFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: `I'm interested in a bulk order for: ${product.name} (SKU: ${product.sku || "N/A"})`,
  })
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false)
  const [bulkSubmitted, setBulkSubmitted] = useState(false)
  const [bulkCountryCode, setBulkCountryCode] = useState("+91")
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false)


  useEffect(() => {
    const payload = JSON.stringify({
      eventType: "product_view",
      productId: product.id,
      eventData: {
        name: product.name,
        category: product.category,
        model: product.model,
      },
    })

    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/analytics/event", payload)
      } else {
        fetch("/api/analytics/event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch((err) => console.warn("Failed to track product view:", err))
      }
    } catch (error) {
      console.warn("Failed to track product view:", error)
    }
  }, [product.id, product.name, product.category, product.model])



  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url || undefined,
      })
    }
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  const handleAddToWishlist = () => {
    addToWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || undefined,
    })
  }

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsBulkSubmitting(true)

    try {
      const fullPhone = `${bulkCountryCode} ${bulkFormData.phone}`.trim()
      const { error } = await supabase
        .from("contact_inquiries")
        .insert({
          name: bulkFormData.name,
          email: bulkFormData.email,
          phone: fullPhone,
          company: bulkFormData.company,
          subject: "Bulk Order",
          message: bulkFormData.message,
        })

      if (error) throw error

      setBulkSubmitted(true)
      toast.success("Bulk order inquiry sent!")
      setTimeout(() => {
        setIsBulkDialogOpen(false)
        setBulkSubmitted(false)
        setBulkFormData((prev: any) => ({
          ...prev,
          name: "",
          email: "",
          phone: "",
          company: "",
          message: `I'm interested in a bulk order for: ${product.name} (SKU: ${product.sku || "N/A"})`,
        }))
      }, 2000)

    } catch (error) {
      console.error("Bulk submission failed:", error)
      toast.error("Failed to send inquiry. Please try again.")
    } finally {
      setIsBulkSubmitting(false)
    }
  }


  const incrementQuantity = () => {
    if (quantity < product.stock_quantity) {
      setQuantity(quantity + 1)
    }
  }

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-[98%] px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link
          href="/products"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("nav.products")}
        </Link>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Product Image Gallery */}
          <div className="flex flex-col gap-4">
            <div className="aspect-square w-full max-w-md overflow-hidden rounded-lg bg-muted mx-auto relative">
              <ImageWithFallback
                src={selectedImage}
                alt={product.name}
                fill
                priority
                fetchPriority="high"
                sizes="(max-width: 768px) 100vw, 500px"
                className="object-cover"
              />
            </div>

            {/* Thumbnails */}
            {(allImages.length > 1) && (
              <div className="grid grid-cols-4 gap-2 w-full">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(img)}
                    className={`relative aspect-square w-full overflow-hidden rounded-md border-2 ${selectedImage === img ? "border-primary" : "border-transparent"
                      }`}
                  >
                    <ImageWithFallback
                      src={img}
                      alt={`${product.name} view ${idx + 1}`}
                      fill
                      sizes="100px"
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl mb-4">{product.name}</h1>




            <span className="text-3xl font-bold text-foreground">
              <Price amount={product.price} />
            </span>
            <p className="text-sm text-muted-foreground mb-6 italic">
              {t("products.dynamic_price")}
            </p>


            <div className="flex items-center gap-2 mb-6">
              <div className={`h-3 w-3 rounded-full ${product.stock_quantity > 0 ? "bg-red-500" : "bg-gray-300"}`}></div>
              <span className="text-red-600 font-medium">
                {product.stock_quantity > 0
                  ? (t("products.only_available") || `Only ${product.stock_quantity} available - order now!`).replace("{count}", product.stock_quantity.toString())
                  : t("products.out_of_stock")}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4 text-sm">
              <span className="font-semibold">{t("products.condition") || "Condition"}:</span>
              <span>{t("products.condition_used") || "Used"}</span>
            </div>

            {/* Trust Signals */}
            <div className="space-y-2 mb-8">
              <div className="flex items-center gap-2 text-sm">
                <div className="rounded-full bg-green-500 p-0.5">
                  <Check className="h-3 w-3 text-white" />
                </div>
                <span>{t("products.trust_payment")}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="rounded-full bg-green-500 p-0.5">
                  <Check className="h-3 w-3 text-white" />
                </div>
                <span>{t("products.trust_shipping")}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="rounded-full bg-green-500 p-0.5">
                  <Check className="h-3 w-3 text-white" />
                </div>
                <span>{t("products.trust_delivery")}</span>
              </div>
            </div>

            {/* Availability Box */}
            <Card className="bg-muted/30 border-muted mb-8">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{t("products.in_stock")}</p>
                  <p className="font-medium text-sm text-green-600">
                    {product.stock_quantity > 0 ? `${product.stock_quantity} ${t("wishlist.items")}` : t("products.out_of_stock")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("products.sku")}</p>
                  <p className="font-mono text-sm font-medium">{product.sku || "N/A"}</p>
                </div>
              </CardContent>
            </Card>


            {/* Quantity Selector */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {/* ... (Quantity selector code) */}

                <span className="text-sm font-medium">{t("products.quantity")}:</span>
                <div className="flex items-center gap-2 rounded-lg border">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="h-10 w-10"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center font-medium">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={incrementQuantity}
                    disabled={quantity >= product.stock_quantity}
                    className="h-10 w-10"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                size="lg"
                className={`w-full gap-2 transition-colors ${addedToCart ? "bg-green-600 hover:bg-green-700" : "bg-[#3462c0] hover:bg-[#3462c0]/90"
                  }`}


                onClick={handleAddToCart}
                disabled={product.stock_quantity === 0}
              >
                <ShoppingCart className="h-5 w-5" />
                {addedToCart ? t("products.added") : t("products.add_to_cart")}
              </Button>

              <Button size="lg" variant="outline" className="w-full bg-transparent" onClick={handleAddToWishlist}>
                <Heart className={`h-5 w-5 mr-2 ${isInWishlist ? "fill-current" : ""}`} />
                {isInWishlist ? t("wishlist.added") || "Added to Wishlist" : t("wishlist.add") || "Add to Wishlist"}
              </Button>

              <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="w-full bg-transparent">
                    Contact for Bulk Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t("products.bulk_title")}</DialogTitle>
                  </DialogHeader>
                  {bulkSubmitted ? (
                    <div className="py-12 text-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <Check className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold">Inquiry Sent!</h3>
                      <p className="text-muted-foreground">We'll get back to you within 24 hours.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleBulkSubmit} className="space-y-4 pt-4">
                      <div className="grid gap-2">
                        <Label htmlFor="bulk-name">Name</Label>
                        <Input
                          id="bulk-name"
                          placeholder="Your Name"
                          value={bulkFormData.name}
                          onChange={(e) => setBulkFormData((prev: any) => ({ ...prev, name: e.target.value }))}
                          required

                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="bulk-email">Email</Label>
                        <Input
                          id="bulk-email"
                          type="email"
                          placeholder="your@email.com"
                          value={bulkFormData.email}
                          onChange={(e) => setBulkFormData((prev: any) => ({ ...prev, email: e.target.value }))}
                          required

                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="bulk-phone">Phone</Label>
                        <div className="flex gap-2">
                          <Select value={bulkCountryCode} onValueChange={setBulkCountryCode}>
                            <SelectTrigger className="w-[100px]">
                              <SelectValue placeholder="Code" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="+91">🇮🇳 +91</SelectItem>
                              <SelectItem value="+1">🇺🇸 +1</SelectItem>
                              <SelectItem value="+44">🇬🇧 +44</SelectItem>
                              <SelectItem value="+49">🇩🇪 +49</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            id="bulk-phone"
                            type="tel"
                            placeholder="Phone number"
                            className="flex-1"
                            value={bulkFormData.phone}
                            onChange={(e) => setBulkFormData((prev: any) => ({ ...prev, phone: e.target.value }))}
                            required

                          />
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="bulk-company">Company (Optional)</Label>
                        <Input
                          id="bulk-company"
                          placeholder="Your Company Name"
                          value={bulkFormData.company}
                          onChange={(e) => setBulkFormData((prev: any) => ({ ...prev, company: e.target.value }))}
                        />

                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="bulk-message">Message</Label>
                        <Textarea
                          id="bulk-message"
                          placeholder="Tell us about your requirements..."
                          className="min-h-[100px]"
                          value={bulkFormData.message}
                          onChange={(e) => setBulkFormData((prev: any) => ({ ...prev, message: e.target.value }))}
                          required

                        />
                      </div>
                      <Button type="submit" className="w-full bg-primary" disabled={isBulkSubmitting}>
                        {isBulkSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          "Send Inquiry"
                        )}
                      </Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {/* Product Specs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("products.info")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">SKU</p>
                    <p className="font-mono text-sm font-medium">{product.sku}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{product.category}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Additional Info Tabs */}
      <div className="mt-12">
        <Tabs defaultValue="description" className="w-full">
          <TabsList>
            <TabsTrigger value="description">{t("products.desc")}</TabsTrigger>
            <TabsTrigger value="specs">{t("products.specs")}</TabsTrigger>
            <TabsTrigger value="shipping">{t("products.shipping")}</TabsTrigger>
          </TabsList>
          <TabsContent value="description" className="mt-6 space-y-4">
            <p className="text-base text-muted-foreground">{product.description}</p>
            <p className="text-base text-muted-foreground">
              This product is a high-quality industrial component suitable for demanding applications. It has been
              rigorously tested and meets international standards for reliability and performance.
            </p>
          </TabsContent>
          <TabsContent value="specs" className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quality Assurance</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  All products meet ISO 9001 standards and have been tested for durability and reliability.
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Warranty</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  12-month manufacturer warranty included. Extended warranties available upon request.
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="shipping" className="mt-6 space-y-4">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Domestic Shipping</h4>
                <p className="text-sm text-muted-foreground">
                  Orders typically ship within 2-3 business days. Delivery time is 3-5 business days.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">International Shipping</h4>
                <p className="text-sm text-muted-foreground">
                  We ship worldwide. International orders may take 7-14 business days for delivery.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  )
}
